import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { GoogleMap, Marker, LoadScript, Polyline } from '@react-google-maps/api';
import { doc, onSnapshot } from "firebase/firestore";
import { db } from '../firebase';
import { FaCar, FaUser } from 'react-icons/fa';
import ReactDOMServer from 'react-dom/server';

// Set the container style for the map.
const containerStyle = {
  width: '100%',
  height: '100vh'
};

// Default center (if no data is available yet).
const defaultCenter = {
  lat: 20.5937,
  lng: 78.9629
};

// Helper function to decode a polyline string.
function decodePolyline(encoded) {
  let points = [];
  let index = 0, len = encoded.length;
  let lat = 0, lng = 0;

  while (index < len) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    let dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    let dlng = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lng += dlng;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

// Generate custom marker icons as SVG data URLs using react-icons.
const driverIconDataUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
  ReactDOMServer.renderToStaticMarkup(<FaCar color="blue" size={32} />)
);
const travellerIconDataUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
  ReactDOMServer.renderToStaticMarkup(<FaUser color="red" size={32} />)
);

const RideMap = () => {
  const { rideId } = useParams();
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(14);
  const [driverMarker, setDriverMarker] = useState(null);
  const [travellerMarker, setTravellerMarker] = useState(null);
  const [polylinePath, setPolylinePath] = useState([]);
  const [startPoint, setStartPoint] = useState(null);
  const [endPoint, setEndPoint] = useState(null);

  useEffect(() => {
    // Create a reference to the ride document.
    const rideDocRef = doc(db, "rides", rideId);
    // Subscribe to real-time updates.
    const unsubscribe = onSnapshot(rideDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        let bounds = new window.google.maps.LatLngBounds();

        // Update driver tracking marker.
        if (data.driverTracking) {
          const driverLoc = { lat: data.driverTracking.lat, lng: data.driverTracking.lng };
          setDriverMarker(driverLoc);
          bounds.extend(driverLoc);
        } else {
          setDriverMarker(null);
        }

        // Update traveller tracking marker.
        if (data.travellerTracking) {
          const travellerLoc = { lat: data.travellerTracking.lat, lng: data.travellerTracking.lng };
          setTravellerMarker(travellerLoc);
          bounds.extend(travellerLoc);
        } else {
          setTravellerMarker(null);
        }

        // If routePolyline exists, decode and update polyline path.
        if (data.routePolyline) {
          const decodedPath = decodePolyline(data.routePolyline);
          setPolylinePath(decodedPath);
          if (decodedPath.length > 0) {
            setStartPoint(decodedPath[0]);
            setEndPoint(decodedPath[decodedPath.length - 1]);

            bounds.extend(decodedPath[0]); // Start location
            bounds.extend(decodedPath[decodedPath.length - 1]); // End location
          }
        } else {
          setPolylinePath([]);
          setStartPoint(null);
          setEndPoint(null);
        }

        // Auto-adjust map to fit all markers and polyline
        setMapCenter(bounds.getCenter().toJSON());
        setMapZoom(12);
      }
    });

    return () => unsubscribe();
  }, [rideId]);

  return (
    <LoadScript googleMapsApiKey="AIzaSyDPYdcbLY9lr7im6rIE2uD18foVP786-fI">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={mapZoom}
      >
        {/* Render the polyline if available */}
        {polylinePath.length > 0 && (
          <Polyline
            path={polylinePath}
            options={{
              strokeColor: "#0000FF",
              strokeOpacity: 0.8,
              strokeWeight: 4,
            }}
          />
        )}

        {/* Starting location marker (green) */}
        {startPoint && (
          <Marker 
            position={startPoint}
            label="Start"
            icon="http://maps.google.com/mapfiles/ms/icons/green-dot.png"
          />
        )}

        {/* Destination marker (red) */}
        {endPoint && (
          <Marker 
            position={endPoint}
            label="End"
            icon="http://maps.google.com/mapfiles/ms/icons/red-dot.png"
          />
        )}

        {/* Driver tracking marker using react-icon */}
        {driverMarker && (
          <Marker 
            position={driverMarker} 
            label="Driver"
            icon={driverIconDataUrl}
          />
        )}

        {/* Traveller tracking marker using react-icon */}
        {travellerMarker && (
          <Marker 
            position={travellerMarker} 
            label="Traveller"
            icon={travellerIconDataUrl}
          />
        )}
      </GoogleMap>
    </LoadScript>
  );
};

export default RideMap;
